import { BaseSelect, BaseTextInput } from "src/components";
import { CashReceiptFormProps, DocumentProofRequest } from "../types";
import { onlyNumber, validatePhoneNumberBoolean } from "src/utils/common-util";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getExpenseProofAsync } from "src/api/contract/contract-api";
import { useApiOperation } from "src/api/hooks";
import { useParams } from "react-router-dom";

// 현금영수증 폼 컴포넌트
// 현금영수증 발급을 위한 정보 입력 폼을 제공하는 컴포넌트
export const CashReceiptForm = ({ handleClickExpenseProofCashBill }: CashReceiptFormProps) => {
  const { t } = useTranslation("contract");
  // 에러 상태 관리
  const [cashErrorTextFlag1, setCashErrorTextFlag1] = useState(false); // 휴대폰 번호 형식 에러
  const [cashErrorTextFlag2, setCashErrorTextFlag2] = useState(false); // 카드번호 형식 에러
  const [cashErrorTextFlag3, setCashErrorTextFlag3] = useState(false); // 사업자번호 형식 에러
  const [cashErrorTextFlag4, setCashErrorTextFlag4] = useState(false); // 필수 입력 에러

  // URL 파라미터에서 계약 ID 가져오기
  const { contractId } = useParams();

  // 증빙서류 요청 정보 상태 관리
  const [documentProofRequest, setDocumentProofRequest] = useState<DocumentProofRequest>({
    identityNum: "",
    mediaList: [],
    taxInvoiceEmail: "",
    taxInvoicePhone: "",
    taxInvoiceDate: null,
    identityType: "",
    tradeUsage: "",
    contractId: Number(contractId),
    cmdType: "",
    expenseProofType: "",
  });

  // 지출 증빙 자료 조회 API 호출
  const { executeAsync: getExpenseProof } = useApiOperation(getExpenseProofAsync);

  // 계약 정보 조회 함수
  const fetchContract = useCallback(
    async (contractId: string) => {
      const { data: data2 } = await getExpenseProof({ contractId });

      if (data2.data.expenseProofRequest.mediaList !== null) {
        data2.data.expenseProofRequest.mediaList.forEach((dt: any) => {
          dt["cmdType"] = "U";
        });
        setDocumentProofRequest(data2.data.expenseProofRequest);
      }
    },
    [getExpenseProof],
  );

  // 컴포넌트 마운트 시 계약 정보 조회
  useEffect(() => {
    if (contractId) {
      fetchContract(contractId);
    }
  }, [fetchContract, contractId]);

  // 신분증 타입 값 생성 함수
  const valueMaker = (documentProofRequest: any) => {
    if (!documentProofRequest || !documentProofRequest.identityType) {
      return null;
    }
    if (documentProofRequest.identityType.includes("CARD_NUM")) {
      if (documentProofRequest.tradeUsage === "INCOME") {
        return "I_CARD_NUM";
      }
      if (documentProofRequest.tradeUsage === "EXPENSE") {
        return "E_CARD_NUM";
      }
    } else {
      return documentProofRequest.identityType;
    }
  };

  // 에러 플래그 초기화 함수
  const resetErrorFlags = () => {
    setCashErrorTextFlag1(false);
    setCashErrorTextFlag2(false);
    setCashErrorTextFlag3(false);
    setCashErrorTextFlag4(false);
  };

  // 에러 플래그 설정 함수
  const setErrorFlags = (flag1: boolean, flag2: boolean, flag3: boolean, flag4: boolean) => {
    setCashErrorTextFlag1(flag1);
    setCashErrorTextFlag2(flag2);
    setCashErrorTextFlag3(flag3);
    setCashErrorTextFlag4(flag4);
  };

  return (
    <div className="base-layout-contents">
      <div className="contract-page h-100">
        {/* 신분증 타입 선택 */}
        <div className="container">
          <BaseSelect
            className="identity-type"
            wrapClassName="text-left"
            value={valueMaker(documentProofRequest)}
            options={[
              { label: t("detail.transactionType.personalMobile"), value: "MOBILE_NUM", type: "INCOME" },
              { label: t("detail.transactionType.personalCard"), value: "I_CARD_NUM", type: "INCOME" },
              { label: t("detail.transactionType.businessNumber"), value: "CORP_NUM", type: "EXPENSE" },
              { label: t("detail.transactionType.businessCard"), value: "E_CARD_NUM", type: "EXPENSE" },
            ]}
            getObj={true}
            onChange={(option: any) => {
              setDocumentProofRequest({
                ...documentProofRequest,
                identityType: option.value.includes("CARD_NUM") ? "CARD_NUM" : option.value,
                tradeUsage: option.type,
                identityNum: "",
              } as DocumentProofRequest);
            }}
            placeholder="신청 유형을 입력해 주세요 (필수)"
          />
        </div>

        {/* 신분증 번호 입력 */}
        <div className="container">
          <BaseTextInput
            value={documentProofRequest?.identityNum}
            onChange={(value: string | undefined) => {
              setDocumentProofRequest({
                ...documentProofRequest,
                identityNum: onlyNumber(String(value)),
              });
              if (value) {
                resetErrorFlags();
              }
            }}
            onBlur={() => {
              const { identityNum, identityType } = documentProofRequest;

              if (identityNum === "") {
                setErrorFlags(false, false, false, true);
                return;
              }

              if (identityType === "MOBILE_NUM" && !validatePhoneNumberBoolean(identityNum)) {
                setErrorFlags(true, false, false, false);
                return;
              }

              if (identityType?.includes("CARD_NUM") && (identityNum.length < 13 || identityNum.length > 19)) {
                setErrorFlags(false, true, false, false);
                return;
              }

              if (identityType === "CORP_NUM" && identityNum.length !== 10) {
                setErrorFlags(false, false, true, true);
                return;
              }

              resetErrorFlags();
            }}
            placeholder={t("detail.documentationNumberPlaceholder")}
          />
          {/* 에러 메시지 표시 */}
          {cashErrorTextFlag1 && <p className="mt5 font12 text-red">휴대폰 번호 형식이 아닙니다.</p>}
          {cashErrorTextFlag2 && <p className="mt5 font12 text-red">카드번호 형식이 아닙니다.</p>}
          {cashErrorTextFlag3 && <p className="mt5 font12 text-red">사업자번호 형식이 아닙니다.</p>}
        </div>
      </div>

      {/* 확인 버튼 */}
      <div className="base-floating-btn-wrap px0">
        <div className="container">
          <button
            type="button"
            className="base-btn primary-btn"
            disabled={
              documentProofRequest!.identityType === "" ||
              documentProofRequest!.identityType === null ||
              documentProofRequest!.identityNum === null ||
              documentProofRequest!.identityNum === "" ||
              cashErrorTextFlag1 ||
              cashErrorTextFlag2 ||
              cashErrorTextFlag3 ||
              cashErrorTextFlag4
            }
            onClick={() => handleClickExpenseProofCashBill(documentProofRequest!)}
          >
            {t("common:confirm")}
          </button>
        </div>
      </div>
    </div>
  );
};
